import { PublicFooter, PublicHeader } from "../components/PublicPageChrome";

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="font-heading font-semibold text-lg text-ink">{title}</h2>
    <div className="text-sm text-ink/70 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-cream text-ink font-body">
      <PublicHeader maxWidthClass="max-w-2xl" showSignIn={false} />
      <main className="max-w-2xl mx-auto px-5 pb-16 space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Legal</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-1">Privacy Policy</h1>
          <p className="text-sm text-ink/50 mt-2">Last updated 22 October 2026</p>
        </div>

        <Section title="What this covers">
          <p>
            This explains what personal data Mnandi collects when you use mnandi.app, why, and who we share it with.
            We only collect what's needed to run the costing/quoting service.
          </p>
        </Section>

        <Section title="What we collect">
          <p><strong>Account data:</strong> name, email address, and (if you sign in with Google) your Google
            account ID, name and email. If you register with a password instead, we store a salted hash of it, never
            the password itself.</p>
          <p><strong>Business data you enter:</strong> ingredient names and prices, receipt photos you upload, voice
            recordings you use for purchase capture, recipes, and quote/pricing details.</p>
          <p><strong>Billing data:</strong> handled by Stripe. We store your subscription status and Stripe
            customer ID, not your card details.</p>
          <p><strong>Usage cookies:</strong> a login session cookie, and, if you arrived via a partner or creator
            link, a short-lived attribution cookie recording which link you came from. Page analytics, when
            switched on, does not add a cookie.</p>
          <p><strong>Analytics:</strong> when this is switched on, we load Umami to count page views and three
            actions: starting sign-up, using the public price-floor calculator, and starting a trial. Umami
            does not use cookies and is not given your name, email, or the recipes and prices you store. We
            send the page path only, not the query string. Umami Cloud can keep this in the EU or the US,
            depending on the account region you choose. A self-hosted Umami stays on that server. If analytics
            is not switched on, the script is not loaded at all.</p>
        </Section>

        <Section title="How we use it">
          <p>To run your account, calculate ingredient costs, recipe costs and quotes, process receipt photos and
            voice notes into structured purchase data, handle billing, and provide support when you email us.</p>
        </Section>

        <Section title="Who we share it with">
          <p>We use a small number of processors to run the service. We don't sell your data.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe:</strong> payment processing and subscription billing.</li>
            <li><strong>Google:</strong> optional "Sign in with Google" authentication.</li>
            <li><strong>Anthropic (Claude):</strong> reads receipt photos and voice transcripts you submit to
              extract structured purchase data (items, quantities, prices).</li>
            <li><strong>AssemblyAI:</strong> transcribes voice notes you record for purchase capture.</li>
            <li><strong>Render:</strong> database and application hosting for Mnandi.</li>
            <li><strong>Umami</strong> (only when analytics is switched on): cookieless page-view counts, as
              described above.</li>
          </ul>
          <p>Each of these processors only receives the data needed to perform their function and is contractually
            restricted from using it for their own purposes.</p>
        </Section>

        <Section title="How long we keep it">
          <p>We keep your account and business data for as long as your account is active. If you close your
            account, we delete your personal data within 30 days, except where we need to keep billing records
            for legal/tax purposes.</p>
        </Section>

        <Section title="Your rights">
          <p>You can ask us to access, correct, export, or delete your personal data at any time by emailing
            <a className="underline" href="mailto:hello@mnandi.app"> hello@mnandi.app</a>. If you're in the UK or
            EU, you also have the right to complain to your local data protection authority (the ICO in the UK).</p>
        </Section>

        <Section title="Contact">
          <p>Questions about this policy or your data: <a className="underline" href="mailto:hello@mnandi.app">hello@mnandi.app</a>.</p>
        </Section>
      </main>
      <PublicFooter maxWidthClass="max-w-2xl" />
    </div>
  );
}
